// ---------------------------------------------------------------------------
// Turn raw Supabase/Postgres errors into something a special-ed TEACHER can
// actually act on. Used by Add Student, incident form, plan form, IEP goal
// form, etc. so we don't dump "new row violates row-level security policy
// for table \"students\"" straight onto the screen.
//
// Guiding principles
// ------------------
// 1. The audience is a teacher. Not a developer, not a DBA. Every message
//    should be one or two sentences of plain English, at a reading level
//    a busy teacher can parse in under ten seconds while a student is
//    tugging at their sleeve.
// 2. Say what happened, in the teacher's world ("your change didn't save",
//    "you got signed out"), NOT what happened in Postgres's world
//    ("42501 row-level security policy violation").
// 3. Say what to try next. "Try again", "refresh the page", or "tell your
//    tech coordinator" — one clear next action.
// 4. Never leak SQL identifiers, table names, or column names to the
//    teacher-facing string. Those are for the console + the tech-details
//    toggle on the SchemaSetupBanner.
// 5. Anything we don't specifically recognize falls through to a generic
//    "something went wrong" — better than dumping raw SQL at a teacher.
//    We still log the raw error to the console for debugging.
// ---------------------------------------------------------------------------

// The generic fallback. Never mentions the word "database" — a teacher
// doesn't know or care about the plumbing.
const GENERIC_FALLBACK =
  'Something went wrong. Please try again in a moment.';

// Categorized short messages, one per class of failure the app sees a
// lot. Keep each ≤ 2 short sentences.
const MESSAGES = {
  schema:
    "This account needs one more setup step before this feature can save. " +
    "Ask your school's tech coordinator to run the setup file (see the yellow banner at the top).",
  rls:
    "The app couldn't save that change — your account doesn't have permission yet. " +
    "Ask your school's tech coordinator to check your access.",
  auth:
    "Looks like you got signed out. Please sign in again and try once more.",
  network:
    "Couldn't reach the app's storage. Check your Wi-Fi and try again in a moment.",
  unique:
    "There's already a record like that. Try refreshing the page — it may have saved the first time.",
  notNull:
    "One of the required fields is blank. Fill it in and try again.",
  foreignKey:
    "The thing you're saving points at something that no longer exists (for example, a student that was removed). " +
    "Refresh the page and try once more.",
};

function extractText(err) {
  if (!err) return '';
  if (typeof err === 'string') return err;
  return (
    err.message ||
    err.error_description ||
    err.error ||
    err.details ||
    err.hint ||
    ''
  );
}

function extractCode(err) {
  if (!err || typeof err !== 'object') return '';
  return err.code || err.status || err?.error?.code || '';
}

// Public helper. Pass any error object, string, or null. Returns a
// user-facing string. Never throws.
export function humanizeSupabaseError(err, fallback) {
  const message = extractText(err).trim();
  const code    = String(extractCode(err));
  const lower   = message.toLowerCase();

  // Always log the raw error for the browser console so a developer
  // debugging with the customer can still see the original text.
  if (err && typeof console !== 'undefined') {
    try { console.error('EduTracker error:', err); } catch { /* ignore */ }
  }

  // Missing table / missing column — the setup-not-run case. Don't
  // even show the raw Postgres string; the SchemaSetupBanner is
  // already up with the actionable version.
  if (
    code === 'PGRST205' ||
    code === 'PGRST204' ||
    code === '42P01' ||
    code === '42703' ||
    lower.includes('could not find the table') ||
    lower.includes('could not find the') && lower.includes('column') ||
    lower.includes('relation') && lower.includes('does not exist') ||
    lower.includes('schema cache')
  ) {
    return MESSAGES.schema;
  }

  // Row Level Security violations. Postgres code 42501.
  if (
    code === '42501' ||
    lower.includes('row-level security') ||
    lower.includes('row level security') ||
    lower.includes('permission denied for')
  ) {
    return MESSAGES.rls;
  }

  // Auth token expired / missing / not authenticated.
  if (
    code === 401 ||
    code === '401' ||
    lower.includes('jwt') ||
    lower.includes('not authenticated') ||
    lower.includes('invalid api key')
  ) {
    return MESSAGES.auth;
  }

  // Network / project paused / DNS / offline.
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('network error') ||
    lower.includes('load failed') ||
    lower.includes('project is paused') ||
    lower.includes('unable to connect')
  ) {
    return MESSAGES.network;
  }

  // Unique constraint.
  if (
    code === '23505' ||
    lower.includes('duplicate key') ||
    lower.includes('unique constraint')
  ) {
    return MESSAGES.unique;
  }

  // NOT NULL. Column names never leak into the teacher-facing string.
  if (code === '23502' || lower.includes('null value in column')) {
    return MESSAGES.notNull;
  }

  // Foreign key.
  if (
    code === '23503' ||
    lower.includes('violates foreign key constraint')
  ) {
    return MESSAGES.foreignKey;
  }

  // We didn't recognize it. Prefer the caller's fallback if they gave
  // us one — it'll be phrased for the specific screen (e.g. "Could
  // not save this incident.") — otherwise the generic message. We
  // deliberately don't return the raw Postgres string; a teacher
  // seeing "duplicate key value violates unique constraint
  // \"students_pkey\"" is worse than seeing a friendly generic.
  return fallback || GENERIC_FALLBACK;
}

export default humanizeSupabaseError;
